import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useMatch, useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { parsePhoneNumber } from 'react-phone-number-input';
import { validateAddEditForm } from './validations';
import DeveloperDetailsTab from './components/DeveloperDetailsTab';
import PropertiesTab from './components/PropertiesTab';
import Tabs from './components/Tabs';
import Header from './components/Header';
import Buttons from './components/Buttons';
import useFetch from '../../../hooks/useFetch';
import {
  VALIDATION_FIELD_MAPPING,
} from '../../../utils/appConstant';
import { AddEditApi } from '../../../interfaces/developer.interface';
import { Validation } from '../../../interfaces/common';
import DeveloperService from '../../../services/developer.service';
import CommonService from '../../../services/common.service';
import { Modules, AuthRoutes, RouteParamsTypes } from '../../../enums';
import Spinner from '../../../components/Spinner';
import Toast from '../../../components/Toast';
import Modal from '../../../components/Modal';
import Button from '../../../components/Button';
import { STATUS_CODE } from '../../../utils/apiConstant';

const initialFormData: AddEditApi = {
  name: '',
  alias_name: '',
  email: '',
  phone: '',
  logo: '',
  logoFile: null,
  primary_owner_name: '',
  gst_number: '',
  code: '',
  website: '',
  office_address_1: '',
  office_address_2: '',
  areaId: '',
  cityId: '',
  stateId: '',
  countryId: '',
  pincode: '',
  notes: '',
  is_active: true,
  order_by: 0,
};

const initialValidation: Validation = { validate: true, fields: [] };

const initialDropdownSearchData = {
  city: '',
  state: '',
  country: '',
  area: '',
  property: '',
};

const AddEditView: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { id } = useParams();
  const idRef = useRef(id);

  useEffect(() => {
    idRef.current = id;
  }, [id]);

  const isActiveParam = !id ? 1 : undefined;

  const view = !!useMatch(Modules.developers + AuthRoutes.developers + RouteParamsTypes.view + '/:id');
  const edit = !!useMatch(Modules.developers + AuthRoutes.developers + RouteParamsTypes.edit + '/:id');
  // ... (keep existing state)
  const navigate = useNavigate();

  const [tab, setTab] = useState<'details' | 'properties'>('details');
  const [formData, setFormData] = useState<AddEditApi>(initialFormData);
  const [dropdownSearchData, setDropdownSearchData] = useState<any>(initialDropdownSearchData);
  const [loading, setLoading] = useState(false);
  const [actionTwoLoading, setActionTwoLoading] = useState<boolean>(false);
  const [validation, setValidation] = useState<Validation>(initialValidation);
  const [assignedProperties, setAssignedProperties] = useState<any[]>([]);
  const [availableProperties, setAvailableProperties] = useState<any[]>([]);
  
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; type: string | null }>({
    open: false,
    type: null,
  });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleCloseDelete = () => {
    setDeleteConfirm({ open: false, type: null });
  };

  const handleRemoveLogo = () => {
    if (formData.logoFile) {
        setFormData((prev) => ({ ...prev, logoFile: null }));
        return;
    }
    if (id && formData.logo) {
      setDeleteConfirm({ open: true, type: 'logo' });
    }
  };

  const handleConfirmDelete = async () => {
    if (!id || !deleteConfirm.type) return;

    setDeleteLoading(true);
    try {
      if (deleteConfirm.type === 'logo') {
        await DeveloperService.deleteLogo(id);
        setFormData((prev) => ({ ...prev, logo: '', logoFile: null }));
        toast.success(<Toast message={t('common.messages.fileDeleted')} />, { icon: false });
      }
      handleCloseDelete();
    } catch (error) {
      console.error(error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const { data: cities } = useFetch({
    service: CommonService,
    func: 'citiesLookup',
    payload: {
      searchText: dropdownSearchData.city || '',
    },
    dependency: [dropdownSearchData.city],
  });

  const { data: areas } = useFetch({
    service: CommonService,
    func: 'areaLookup',
    payload: {
      searchText: dropdownSearchData.area || '',
      is_active: isActiveParam
    },
    dependency: [dropdownSearchData.area, isActiveParam],
  });

  const { data: states } = useFetch({
    service: CommonService,
    func: 'statesLookup',
    payload: {
      searchText: dropdownSearchData.state || '',
    },
    dependency: [dropdownSearchData.state],
  });

  const { data: countries } = useFetch({
    service: CommonService,
    func: 'countriesLookup',
    payload: {
      searchText: dropdownSearchData.country || '',
    },
    dependency: [dropdownSearchData.country],
  });

  useEffect(() => {
    if (!id && countries?.length > 0 && !formData.countryId) {
      const india = countries.find(
        (country: any) => country.name.toLowerCase() === 'india'
      );
      if (india) {
        setFormData((prev) => ({
          ...prev,
          countryId: india.id,
        }));
      }
    }
  }, [countries, id, formData.countryId]);

  useEffect(() => {
    if (!id && states?.length > 0 && !formData.stateId) {
      const gujarat = states.find(
        (state: any) => state.name.toLowerCase() === 'gujarat'
      );
      if (gujarat) {
        setFormData((prev) => ({
          ...prev,
          stateId: gujarat.id,
        }));
      }
    }
  }, [states, id, formData.stateId]);

  useEffect(() => {
    setTab(location?.state?.tab ?? 'details');
  }, [location.state, location.key]);

  useEffect(() => {
    setFormData(initialFormData);
    setDropdownSearchData(initialDropdownSearchData);
    setValidation(initialValidation);
    setAssignedProperties([]);
  }, [location.key]);

  const { loading: getContentLoading } = useFetch({
    service: DeveloperService,
    func: 'get',
    payload: id,
    dependency: [id, location.key],
    skip: !id,
    onSuccess: async (response: any) => {
      if (idRef.current !== id) return;
      setFormData({
        ...initialFormData,
        ...response,
        logo: response.logo_url || '',
        areaId: response.area_id,
        cityId: response.city_id,
        stateId: response.state_id,
        countryId: response.country_id,
      });
      setAssignedProperties(response.developer_properties || []);
    },
    onError: (error: any) => {
      if (error.code === STATUS_CODE.notFound) navigate(Modules.developers + AuthRoutes.developers);
    },
  });

  const handleTabChange = useCallback((e: any, value: 'details' | 'properties') => {
    setTab(value);
  }, []);

  const handleInputChange = useCallback(
    (event: any, name: string) => {
        if (name === 'phone') {
          const phoneNumber = parsePhoneNumber(event?.target?.value ?? '');
          setFormData((prev: AddEditApi) => {
            return {
              ...prev,
              phone: phoneNumber && phoneNumber.nationalNumber,
            };
          });
        } else {
          let value: any;
          if (name === 'is_active') {
            value = event.target.checked;
          } else if (name === VALIDATION_FIELD_MAPPING.logoFile) {
            // Handle file input
            value = event.target.value;
          } else {
            value = event.target.value;
          }

          setFormData((prev: AddEditApi) => {
            return {
              ...prev,
              [name]: value,
            };
          });
        }
    },
    [setFormData]
  );

  const handleDropdownSearchInputChange = useCallback(
    (event: any, name: string) => {
      let key = name;
      if (name === VALIDATION_FIELD_MAPPING.countryId) key = 'country';
      else if (name === VALIDATION_FIELD_MAPPING.stateId) key = 'state';
      else if (name === VALIDATION_FIELD_MAPPING.cityId) key = 'city';
      else if (name === VALIDATION_FIELD_MAPPING.area_id) key = 'area';

      setDropdownSearchData((prev: any) => {
        return {
          ...prev,
          [key]: event.target.value,
        };
      });
    },
    [setDropdownSearchData]
  );

  const handleInputBlur = useCallback(
    (event: any, name: string) => {
      let newValidation = validateAddEditForm(
        {
          ...formData,
          [name]: event.target.value.trim(),
        },
        name
      );
      setValidation(newValidation);
    },
    [formData]
  );

  const handleSubmit = useCallback(
    async (action: '1' | '2', event?: React.FormEvent) => {
      event && event.preventDefault();

      const newValidation = validateAddEditForm(formData);
      setValidation(newValidation);
      if (!newValidation.validate) return;

      if (action === '1') setLoading(true);
      else setActionTwoLoading(true);

      try {
        const fd = new FormData();
        fd.append('name', formData.name.trim());
        fd.append('alias_name', formData.alias_name?.trim() || '');
        fd.append('email', formData.email?.trim() || '');
        fd.append('phone', formData.phone?.trim() || '');

        if (formData.logoFile) {
          fd.append('logo', formData.logoFile);
        }

        fd.append('primary_owner_name', formData.primary_owner_name?.trim() || '');
        fd.append('gst_number', formData.gst_number?.trim() || '');
        fd.append('code', formData.code?.trim() || '');
        fd.append('website', formData.website?.trim() || '');
        fd.append('office_address_1', formData.office_address_1?.trim() || '');
        fd.append('office_address_2', formData.office_address_2?.trim() || '');
        fd.append('area_id', formData.areaId || '');
        fd.append('city_id', formData.cityId || '');
        fd.append('state_id', formData.stateId || '');
        fd.append('country_id', formData.countryId || '');
        fd.append('pincode', formData.pincode?.trim() || '');
        fd.append('notes', formData.notes?.trim() || '');
        fd.append('is_active', formData.is_active ? '1' : '0');
        if (!edit) {
          fd.append('order_by', String(formData.order_by || 1));
        }

        let response: any;
        if (!edit) {
          response = await DeveloperService.add(fd);
        } else if (id) {
          response = await DeveloperService.edit(id, fd);
        }

        if (response.success !== false) {
          toast.success(
            <Toast
              message={edit ? t('developers.addEdit.pageTexts.form.editSuccess') : t('developers.addEdit.pageTexts.form.addSuccess')}
            />,
            { icon: false }
          );

          if (edit) {
            navigate(Modules.developers + AuthRoutes.developers);
          } else if (action === '2') {
            navigate(Modules.developers + AuthRoutes.developers + RouteParamsTypes.add, {
              replace: true,
            });
            setFormData(initialFormData);
          } else {
            navigate(`${Modules.developers}${AuthRoutes.developers}`, { replace: true });
          }
        }

        if (action === '1') setLoading(false);
        else setActionTwoLoading(false);
      } catch (err: any) {
        if (action === '1') setLoading(false);
        else setActionTwoLoading(false);
      }
    },
    [id, edit, formData, navigate]
  );

  const handleKeyDown = useCallback(
    (e: any) => {
      if (e.key === 'Enter') handleSubmit('1');
    },
    [handleSubmit]
  );

  const handleCancel = () => {
    navigate(Modules.developers + AuthRoutes.developers);
  };

  // Handlers for properties tab (placeholder - connect to API when ready)
  const handleAssignProperty = useCallback((propertyId: string) => {
    // TODO: Call API to assign property to developer
    console.log('Assign property:', propertyId);
    // After successful API call, refresh the assigned properties list
  }, []);

  const handleRemoveProperty = useCallback((propertyId: string) => {
    // TODO: Call API to remove property assignment from developer
    console.log('Remove property:', propertyId);
    // After successful API call, refresh the assigned properties list
  }, []);

  let formContent;

  if (getContentLoading) {
    formContent = (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  } else if (tab === 'details') {
    formContent = (
      <DeveloperDetailsTab
        edit={edit}
        view={view}
        formData={formData}
        validation={validation}
        dropdownSearchData={dropdownSearchData}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        onKeyDown={handleKeyDown}
        onInputBlur={handleInputBlur}
        onInputChange={handleInputChange}
        cities={cities}
        areas={areas}
        states={states}
        countries={countries}
        onRemoveLogo={handleRemoveLogo}
      />
    );
  } else if (tab === 'properties') {
    formContent = (
      <PropertiesTab
        assignedProperties={assignedProperties}
      />
    );
  }

  return (
    <>
      <Header id={id} view={view} edit={edit} />

      <div className="card mb-6">
        <h5 className="card-header">
          {!id
            ? t('developers.addEdit.pageTexts.title.addTitle')
            : view
              ? t('developers.addEdit.pageTexts.title.viewTitle')
              : t('developers.addEdit.pageTexts.title.editTitle')}
        </h5>

        <hr className="m-0"></hr>

        <Tabs tab={tab} add={!edit && !view} onTabChange={handleTabChange} />

        <hr className="m-0"></hr>

        {formContent}

        <hr className="m-0"></hr>

        {tab === 'details' && (
          <Buttons
            actionTwoLoading={actionTwoLoading}
            loading={loading}
            view={view}
            edit={edit}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
          />
        )}

        <Modal
          open={deleteConfirm.open}
          onClose={handleCloseDelete}
          title={t('common.modal.deleteConfirmationTitle')}
          maxWidth="sm"
          content={
            <div className="text-center">
              <i className="ri-error-warning-line text-warning fs-1 mb-3"></i>
              <p className="mb-0">{t('common.modal.deleteConfirmationMessage')}</p>
            </div>
          }
          action={
            <div className="d-flex justify-content-center gap-3 w-100 pb-3">
              <Button
                className="btn btn-outline-secondary"
                onClick={handleCloseDelete}
                text={t('common.buttons.cancel')}
                disabled={deleteLoading}
              />
              <Button
                className="btn btn-danger"
                onClick={handleConfirmDelete}
                text={t('common.buttons.delete')}
                loading={deleteLoading}
                disabled={deleteLoading}
              />
            </div>
          }
        />
      </div>
    </>
  );
};

export default AddEditView;
